import { FC, FormEventHandler, useState } from "react";
import { ROW_PER_PAGE, StyledTableCell, StyledTableRow, useTablePage } from "utils/table";
import { useDispatch } from "store/hooks";
import { useNavigate } from "react-router-dom";
import { Stack } from "components/layout/Stack";
import { useModalContext } from "components/modal/inside/ModalProvider";
import { ResultModal } from "components/modal/inside/ResultModal";
import {
  Button,
  FormControlLabel,
  Link,
  Paper,
  Switch,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
} from "@mui/material";
import { awdApi } from "store/reducers/awdApi";
import { AirportResponse } from "awd-server-api";
import { toastError, toastMessage } from "store/reducers/toastSlice";
import { describeMutationResult } from "logic/api/result/describe";

type AirportListingTableProps = {};

export const AirportListingTable: FC<AirportListingTableProps> = (props) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [page, setPage, handleChangePage] = useTablePage();
  const [controlledText, setControlledText] = useState("");
  const [searchText, setSearchText] = useState("");
  const [zeroOnly, setZeroOnly] = useState(false);

  const airportListing = awdApi.endpoints.getAirportListing.useQuery({
    page,
    text: searchText,
    zeroOnly,
  });

  const airports = airportListing?.data?.airports;
  const count = airportListing?.data?.count;

  const textSearch: FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault();
    setPage(0);
    setSearchText(controlledText);
  };

  const addAirport = async () => {
    try {
      const result = await dispatch(
        awdApi.endpoints.postAirport.initiate({
          body: {
            name: "<replace>",
            latitude: 0.0,
            longitude: 0.0,
          },
        })
      ).then(describeMutationResult);

      if (result.error) {
        dispatch(toastError(result.error, `Adding new airport went wrong`));
      } else {
        navigate(`/${result.data.id}/edit`);
      }
    } catch (error) {
      dispatch(
        toastMessage("error", "Something went wrong, please try again later.")
      );
    }
  };

  return (
    <div css={{ marginTop: "1rem" }}>
      <Stack
        gap="0"
        horizontal
        css={{ justifyContent: "space-between", marginBottom: "1rem" }}
      >
        <Stack gap="1rem" horizontal css={{ alignItems: "center" }}>
          <Button onClick={addAirport}>Add airport</Button>
          <FormControlLabel
            control={
              <Switch
                checked={zeroOnly}
                onChange={(event) => {
                  setZeroOnly(event.target.checked);
                  setPage(0);
                }}
              />
            }
            label="Show only airports near 0,0"
          />
        </Stack>
        <form onSubmit={textSearch}>
          <TextField
            label="Search via icao / airport name"
            variant="outlined"
            css={{ width: "250px" }}
            value={controlledText}
            onChange={(e) => setControlledText(e.target.value)}
          />
        </form>
      </Stack>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <StyledTableCell>Airport Id</StyledTableCell>
              <StyledTableCell>ICAO</StyledTableCell>
              <StyledTableCell>Airport Name</StyledTableCell>
              <StyledTableCell>Country</StyledTableCell>
              <StyledTableCell>ICAO Alias</StyledTableCell>
              <StyledTableCell>Edit</StyledTableCell>
              <StyledTableCell>Delete</StyledTableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {airports?.map((airport) => <AirportTableRow key={airport.id} airport={airport} />)}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[]}
        component="div"
        count={count ?? 0}
        rowsPerPage={ROW_PER_PAGE}
        page={page}
        onPageChange={handleChangePage}
        showFirstButton
        showLastButton
      />
    </div>
  );
};

type AirportTableRowProps = {
  airport: AirportResponse;
};
const AirportTableRow = ({ airport }: AirportTableRowProps) => {
  const dispatch = useDispatch();
  const { show } = useModalContext();

  const deleteAirport = () => {
    show(
      <ResultModal
        title={`Delete ${airport.name} (${airport.icao})?`}
        submitButton={{ color: "error", text: "Delete" }}
        onSubmit={async () => {
          const result = await dispatch(
            awdApi.endpoints.deleteAirport.initiate({
              airportId: airport.id,
            })
          ).then(describeMutationResult);

          if (result.error) {
            dispatch(
              toastError(result.error, "Deleting the airport went wrong.")
            );
            return "cancel";
          }

          dispatch(toastMessage("success", "Airport deleted."));
          return;
        }}
      />
    );
  };

  return (
    <StyledTableRow>
      <StyledTableCell>{airport.id}</StyledTableCell>
      <StyledTableCell>{airport.icao}</StyledTableCell>
      <StyledTableCell>{airport.name}</StyledTableCell>
      <StyledTableCell>{airport.isoCountry}</StyledTableCell>
      <StyledTableCell>{airport.icaoAlias}</StyledTableCell>
      <StyledTableCell>{<Link href={`/${airport.icao}/edit`}>Edit</Link>}</StyledTableCell>
      <StyledTableCell>
        <Button color="error" onClick={deleteAirport}>
          Delete
        </Button>
      </StyledTableCell>
    </StyledTableRow>
  );
};
